import { useSearchParams } from 'react-router-dom';

import type { PathwayOptionSet } from '../configuration/shared';
import { useProgramContentOverride } from '../contexts/DrugProgramProvider';
import { useFillPrescription } from '../contexts/fill/use-fill-prescription';

import { useDrugKey } from './use-drug-key';
import { getNdc } from './use-medication-pricing';
import { usePathwayOverride } from './use-pathway-override';

export const usePathwayOptions = (): PathwayOptionSet | null => {
    const { medication } = useFillPrescription();
    const drugKey = useDrugKey(medication);
    const ndc = getNdc(medication);
    const contentOverride = useProgramContentOverride();
    const pathwayOverride = usePathwayOverride(medication);
    const [searchParams] = useSearchParams();

    // NDC-specific config takes precedence over drug-key config
    const drugOptions =
        (ndc && contentOverride.pathwayOptionsByNdc?.[ndc]) ||
        (drugKey && contentOverride.pathwayOptions?.[drugKey]) ||
        null;

    if (!drugOptions) return null;

    const subPathway = searchParams.get('sub-pathway');

    if (subPathway === 'medicare-bridge' && drugOptions.medicareBridge) {
        return drugOptions.medicareBridge;
    }

    if (subPathway === 'other-government' && drugOptions.governmentInsurance) {
        return drugOptions.governmentInsurance;
    }

    const pathway = searchParams.get('pathway');

    if (
        (pathway === 'insurance' ||
            pathwayOverride?.favoredPathway === 'insurance') &&
        drugOptions.insuranceFavored
    ) {
        return drugOptions.insuranceFavored;
    }

    if (pathway === 'self-pay' && drugOptions.selfPayFavored) {
        return drugOptions.selfPayFavored;
    }

    return drugOptions.default;
};
